import { Select, Button, Textarea } from "../../../../../../components"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import type { AddressList } from "../../types"
import { useCountry, useProvince, useRegency, useSubdistrict, useVillage } from "../../../../../Setup/Preference/Location/components/FormSection/utils"
import { useEffect } from "react"
import { allowedCountries } from "../../utils"
import { SelectOptionType } from "types"
interface AddressProps {
  action?: "CREATE" | "UPDATE" | "DETAIL"
  onSubmit: (newState: any) => void
  isActiveSubmit?: boolean
  permission?: boolean
}
interface CountryType {
  data: SelectOptionType[]
  isLoading: boolean
}

export const FormAddress:React.FC<AddressProps> = ({ action, onSubmit, isActiveSubmit, permission }) => {
  const { control, getValues, handleSubmit, formState } = useFormContext<AddressList>()
  const country = useCountry()

  return (
    <div>
      <section>
        <Country
          isDisabled={Boolean(action === "DETAIL")}
          country={country}
        />

        <Province
          isDisabled={Boolean(action === "DETAIL")}
          country={country}
        />

        <Regency
          isDisabled={Boolean(action === "DETAIL")}
          country={country}
        />

        <Subdistrict
          isDisabled={Boolean(action === "DETAIL")}
          country={country}
        />

        <Village
          isDisabled={Boolean(action === "DETAIL")}
          country={country}
        />

        <Controller
          control={control}
          name="address"
          render={({ field, fieldState }) => <Textarea {...field} label="ADDRESS" placeholder="Input address" value={field.value ?? ""} error={fieldState.error?.message} disabled={action === "DETAIL"} />}
        />
      </section>
      {action !== "DETAIL" &&
        <div className="flex justify-end">
          <Button 
            type="button"
            className="!none mt-5" 
            color="primary"
            loading={formState.isSubmitting ? "true" : undefined}
            permission={"CT021"}
            onClick={handleSubmit(() => onSubmit(getValues()),(err)=> console.log(err))}
            disabled={isActiveSubmit}
          >
            SAVE
          </Button>
        </div>
      }
    </div>
  )
}

function Country(props: {
  isDisabled: boolean
  country: CountryType
}) {
  const { control, setValue } = useFormContext<AddressList>()

  return (
    <Controller
      control={control}
      name="country_id"
      render={({ field, fieldState }) => (
        <Select
          label="COUNTRY"
          placeholder="Choose country"
          isDisabled={props.isDisabled}
          isLoading={props.country.isLoading}
          error={fieldState.error?.message}
          options={props.country.data}
          value={props.country.data.find(item => item.value === field.value) ?? null}
          onChange={e => {
            field.onChange(e?.value)
            setValue("province_id", undefined)
            setValue("regency_id", undefined)
            setValue("subdistrict_id", undefined)
            setValue("village_id", undefined)
          }}
        />
      )}
    />
  )
}
function Province(props: {
  isDisabled: boolean
  country: CountryType
}) {
  const { control, setValue } = useFormContext<AddressList>()
  const country_id = useWatch({
    control,
    name: "country_id"
  })
  const { data, isLoading, refetch } = useProvince()
  
  // Vars
  const country = props.country.data.find(item => item.value === country_id)?.label.toLowerCase() ?? ""

  useEffect(() => {
    refetch(country_id)
    // eslint-disable-next-line
  }, [country_id])

  return (
    <Controller
      control={control}
      name="province_id"
      render={({ field, fieldState }) => (
        <Select
          label="PROVINCE"
          placeholder="Choose province"
          isDisabled={props.isDisabled || !country_id || !allowedCountries.includes(country)}
          isLoading={isLoading}
          error={fieldState.error?.message}
          options={data}
          value={data.find(item => item.value === field.value) ?? null}
          onChange={e => {
            field.onChange(e?.value)
            setValue("regency_id", undefined)
            setValue("subdistrict_id", undefined)
            setValue("village_id", undefined)
          }}
        />
      )}
    />
  )
}
function Regency(props: {
  isDisabled: boolean
  country: CountryType
}) {
  const { control, setValue } = useFormContext<AddressList>()
  const country_id = useWatch({
    control,
    name: "country_id"
  })
  const province_id = useWatch({
    control,
    name: "province_id"
  })
  const { data, isLoading, refetch } = useRegency()
  
  // Vars
  const country = props.country.data.find(item => item.value === country_id)?.label.toLowerCase() ?? ""

  useEffect(() => {
    refetch(province_id)
    // eslint-disable-next-line
  }, [province_id])

  return (
    <Controller
      control={control}
      name="regency_id"
      render={({ field, fieldState }) => (
        <Select
          label="REGENCY"
          placeholder="Choose regency"
          isDisabled={props.isDisabled || !province_id || !allowedCountries.includes(country)}
          isLoading={isLoading}
          error={fieldState.error?.message}
          options={data}
          value={data.find(item => item.value === field.value) ?? null}
          onChange={e => {
            field.onChange(e?.value)
            setValue("subdistrict_id", undefined)
            setValue("village_id", undefined)
          }}
        />
      )}
    />
  )
}
function Subdistrict(props: {
  isDisabled: boolean
  country: CountryType
}) {
  const { control, setValue } = useFormContext<AddressList>()
  const country_id = useWatch({
    control,
    name: "country_id"
  })
  const regency_id = useWatch({
    control,
    name: "regency_id"
  })
  const { data, isLoading, refetch } = useSubdistrict()
  
  // Vars
  const country = props.country.data.find(item => item.value === country_id)?.label.toLowerCase() ?? ""

  useEffect(() => {
    refetch(regency_id)
    // eslint-disable-next-line
  }, [regency_id])

  return (
    <Controller
      control={control}
      name="subdistrict_id"
      render={({ field, fieldState }) => (
        <Select
          label="SUBDISTRICT"
          placeholder="Choose subdistric"
          isDisabled={props.isDisabled || !regency_id || !allowedCountries.includes(country)}
          isLoading={isLoading}
          error={fieldState.error?.message}
          options={data}
          value={data.find(item => item.value === field.value) ?? null}
          onChange={e => {
            field.onChange(e?.value)
            setValue("village_id", undefined)
          }}
        />
      )}
    />
  )
}
function Village(props: {
  isDisabled: boolean
  country: CountryType
}) {
  const { control } = useFormContext<AddressList>()
  const country_id = useWatch({
    control,
    name: "country_id"
  })
  const subdistrict_id = useWatch({
    control,
    name: "subdistrict_id"
  })
  const { data, isLoading, refetch } = useVillage()
  
  // Vars
  const country = props.country.data.find(item => item.value === country_id)?.label.toLowerCase() ?? ""

  useEffect(() => {
    refetch(subdistrict_id)
    // eslint-disable-next-line
  }, [subdistrict_id])

  return (
    <Controller
      control={control}
      name="village_id"
      render={({ field, fieldState }) => (
        <Select
          label="VILLAGE"
          placeholder="Choose village"
          isDisabled={props.isDisabled || !subdistrict_id || !allowedCountries.includes(country)}
          isLoading={isLoading}
          error={fieldState.error?.message}
          options={data}
          value={data.find(item => item.value === field.value) ?? null}
          onChange={e => field.onChange(e?.value)}
        />
      )}
    />
  )
}